import { useCallback } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { useCurrentUser } from "./useCurrentUser";
import { useToast } from "@/components/ui/toast";
import type { Id } from "convex/_generated/dataModel";

/** The subset of an event this hook needs to work out what it follows. */
type FavoritableEvent = {
  _id: string;
  patternId?: string;
};

/**
 * Following, for the signed-in user.
 *
 * Backed by the `favorites` table rather than localStorage, which is what lets
 * a favorite mean something to the server — the venue notice fan-out reads the
 * same rows. It also makes following account-only: signed out, everything here
 * reports "not following" and `canFavorite` is false, so callers can render a
 * sign-in prompt instead of a control that would silently do nothing.
 *
 * A recurring event is followed as its series, so `isEventFavorited` takes the
 * event rather than an id — it has to check `patternId` before `_id`.
 */
export function useFavorites() {
  const { isLoggedIn } = useCurrentUser();
  const { toast } = useToast();
  const favorites = useQuery(api.favorites.myFavoriteIds);
  const toggleEvent = useMutation(api.favorites.toggleEvent);
  const toggle = useMutation(api.favorites.toggle);

  const ids = favorites ?? {
    eventIds: [],
    seriesIds: [],
    venueIds: [],
    organizerIds: [],
  };

  const isEventFavorited = useCallback(
    (event: FavoritableEvent) =>
      event.patternId
        ? ids.seriesIds.includes(event.patternId)
        : ids.eventIds.includes(event._id),
    [ids.seriesIds, ids.eventIds]
  );

  const isVenueFavorited = useCallback(
    (venueId: string | undefined) =>
      !!venueId && ids.venueIds.includes(venueId),
    [ids.venueIds]
  );

  const isOrganizerFavorited = useCallback(
    (organizerId: string | undefined) =>
      !!organizerId && ids.organizerIds.includes(organizerId),
    [ids.organizerIds]
  );

  /**
   * Confirms the result of a toggle. The toast lives here rather than at each
   * call site so every following control reports itself the same way, and so
   * the wording follows the server's `following` value rather than the local
   * guess the button made before the mutation returned.
   */
  const confirm = useCallback(
    (following: boolean, noun: string, name?: string) => {
      toast(following ? `Following this ${noun}` : `Unfollowed this ${noun}`, {
        variant: following ? "follow" : "unfollow",
        description: name,
      });
    },
    [toast]
  );

  const fail = useCallback(
    (error: unknown, noun: string) => {
      // Convex wraps the thrown message; show something human either way.
      const message = error instanceof Error ? error.message : "";
      toast(`Couldn't update this ${noun}`, {
        variant: "error",
        description: message.includes("not found")
          ? "It may have been removed."
          : "Check your connection and try again.",
      });
    },
    [toast]
  );

  const toggleEventFavorite = useCallback(
    async (eventId: string, name?: string) => {
      try {
        const result = await toggleEvent({ eventId: eventId as Id<"events"> });
        // A recurring occurrence follows its whole series, so say so — otherwise
        // the user expects to have followed only the date they tapped.
        confirm(
          result.following,
          result.targetType === "series" ? "series" : "event",
          name
        );
      } catch (error) {
        fail(error, "event");
      }
    },
    [toggleEvent, confirm, fail]
  );

  const toggleVenueFavorite = useCallback(
    async (venueId: string, name?: string) => {
      try {
        const result = await toggle({ targetType: "venue", targetId: venueId });
        confirm(result.following, "venue", name);
      } catch (error) {
        fail(error, "venue");
      }
    },
    [toggle, confirm, fail]
  );

  const toggleOrganizerFavorite = useCallback(
    async (organizerId: string, name?: string) => {
      try {
        const result = await toggle({
          targetType: "organizer",
          targetId: organizerId,
        });
        confirm(result.following, "organizer", name);
      } catch (error) {
        fail(error, "organizer");
      }
    },
    [toggle, confirm, fail]
  );

  return {
    favorites: ids,
    /** False when signed out — following requires an account. */
    canFavorite: isLoggedIn,
    isLoading: favorites === undefined,
    isEventFavorited,
    isVenueFavorited,
    isOrganizerFavorited,
    toggleEventFavorite,
    toggleVenueFavorite,
    toggleOrganizerFavorite,
  };
}
